import {useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {LoaderCircle} from 'lucide-react';
export interface AppBlockUIProps {
  blocked?: boolean;
}
export function AppBlockUI({blocked}: AppBlockUIProps) {
  const previousFocus = useRef<HTMLElement | null>(null);
  const overlay = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!blocked) return;
    const root = document.getElementById('root');
    const previousInert = new Map<HTMLElement, boolean>();
    const blockBackground = () => {
      Array.from(document.body.children).forEach((element) => {
        if (element instanceof HTMLElement && element !== overlay.current && !previousInert.has(element)) {
          previousInert.set(element, element.inert);
          element.inert = true;
        }
      });
    };
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    blockBackground();
    const observer = new MutationObserver(blockBackground);
    observer.observe(document.body, {childList: true});
    if (root) root.setAttribute('aria-busy', 'true');
    overlay.current?.focus();
    return () => {
      observer.disconnect();
      previousInert.forEach((inert, element) => {
        element.inert = inert ?? false;
      });
      root?.removeAttribute('aria-busy');
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
    };
  }, [blocked]);
  return blocked
    ? createPortal(
        <div
          data-testid="app-loading-overlay"
          ref={overlay}
          role="status"
          aria-label="Loading"
          tabIndex={-1}
          className="fixed inset-0 z-[200] grid place-items-center bg-black/30 backdrop-blur-[1px] outline-none"
          onKeyDown={(event) => {
            if (event.key === 'Tab') event.preventDefault();
          }}
        >
          <LoaderCircle className="size-12 animate-spin text-white" />
          <span className="sr-only">Please wait</span>
        </div>,
        document.body,
      )
    : null;
}
